import { useState } from 'react';
import { Plus, Pencil, TriangleAlert, CircleCheck, Percent, History, ArrowRight } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { TwDataTable, type DataTableColumn } from '../../components/TwDataTable';
import { useServerSort } from '../../hooks/useServerSort';
import {
  PageHeader, Card, Button, IconButton, Modal, Form, useForm, Input, InputNumber, DatePicker, Row, Col, Tag,
  Select, Alert, Tooltip, message, StatCard,
} from '../../ui';

interface CoverageRow {
  productId: string; sku: string; name: string; variantLabel: string | null;
  hsn: string | null; problem: 'NO_HSN' | 'NOT_CONFIGURED' | null;
  taxStatus: string | null; ratePct: number | null; effectiveFrom: string | null; blocked: boolean;
}

export function TaxMastersPage() {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  /** HSN to pre-fill when the form is opened from a product that has no rate. */
  const [prefillHsn, setPrefillHsn] = useState<string | null>(null);
  const canManage = usePermission('TAX_MASTER.ADMIN'); // create/edit needs ADMIN; VIEW is read-only

  // Sorting is the backend's ORDER BY (see useServerSort) so every caller of this endpoint sees
  // the same order, and the sort survives a reload or a Back out of a row — it lives in the URL.
  const { sortState, onSortChange, sort } = useServerSort();

  const { data, isLoading } = useQuery({
    queryKey: ['tax-masters-all', sort],
    queryFn: () => request<any[]>({ url: '/tax-masters', params: sort ? { sort } : {} }),
  });
  // The gap between this master and the catalogue: which products the GST engine cannot price.
  const { data: coverage } = useQuery({
    queryKey: ['tax-coverage'],
    queryFn: () => request<CoverageRow[]>({ url: '/tax-masters/coverage' }),
  });
  const blocked = (coverage ?? []).filter((c) => c.blocked);
  // One row per HSN that needs configuring — that is the unit of work, not one row per product.
  const blockedHsns = Array.from(new Set(blocked.map((c) => c.hsn).filter(Boolean) as string[]));
  const noHsn = blocked.filter((c) => !c.hsn);

  // What the master actually covers — the register below lists rows, these say what they are worth.
  const rates = data ?? [];
  const inForce = rates.filter((r: any) => !r.effectiveTo).length;
  const superseded = rates.length - inForce;
  const distinctHsns = new Set(rates.map((r: any) => r.hsnCode).filter(Boolean)).size;
  const priced = coverage ? coverage.length - blocked.length : 0;

  /*
   * The column KEYS are fixed: `storageKey="tax-masters"` persists a per-user visibility allow-list,
   * so a renamed or added key would ship hidden to everyone who has already used this page. Only the
   * RENDERING changes below.
   */
  const columns: DataTableColumn<any>[] = [
    {
      key: 'hsnCode', title: 'HSN', dataIndex: 'hsnCode', sortable: true, alwaysVisible: true,
      render: (v: any) => <span className="font-mono type-body-medium text-content">{v}</span>,
    },
    { key: 'description', title: 'Description', dataIndex: 'description', sortable: true, alwaysVisible: true },
    // CGST and SGST are the two HALVES of the intra-state rate — never read alone, and never
    // different from each other. Rendered quietly as halves so the eye goes to the rate that
    // actually gets charged rather than treating three numbers as three independent facts.
    {
      key: 'cgstPct', title: 'CGST', dataIndex: 'cgstPct', sortable: true, align: 'right',
      render: (v: any) => <span className="tabular-nums text-muted">{v}%</span>,
    },
    {
      key: 'sgstPct', title: 'SGST', dataIndex: 'sgstPct', sortable: true, align: 'right',
      render: (v: any) => <span className="tabular-nums text-muted">{v}%</span>,
    },
    {
      key: 'igstPct', title: 'Rate', dataIndex: 'igstPct', sortable: true, align: 'right', alwaysVisible: true,
      render: (v: any, r: any) => (
        <span className="inline-flex flex-col items-end leading-tight">
          <span className="type-body-strong tabular-nums text-content">{v}%</span>
          <span className="type-caption text-subtle">{Number(r.cgstPct ?? 0)}+{Number(r.sgstPct ?? 0)} within state</span>
        </span>
      ),
    },
    {
      key: 'taxStatus', title: 'Treatment', dataIndex: 'taxStatus', sortable: true, align: 'center', width: 130,
      // Always a chip. "taxable" as grey text repeated down a column read as an absence of
      // information rather than as the deliberate, most common treatment.
      render: (v: string) => (v && v !== 'TAXABLE'
        ? <Tag tone="gold">{String(v).replace('_', '-').toLowerCase()}</Tag>
        : <Tag tone="neutral">taxable</Tag>),
    },
    {
      key: 'effectiveFrom', title: 'From', dataIndex: 'effectiveFrom', sortable: true,
      render: (v: string) => <span className="tabular-nums">{v ? dayjs(v).format('DD MMM YYYY') : '—'}</span>,
    },
    {
      key: 'effectiveTo', title: 'To', dataIndex: 'effectiveTo',
      render: (v: string) => (v
        ? <span className="tabular-nums text-muted">{dayjs(v).format('DD MMM YYYY')}</span>
        : <Tag tone="green">Current</Tag>),
    },
    ...(canManage ? [{
      key: 'actions', title: '', alwaysVisible: true, width: 70,
      render: (_: any, r: any) => (
        <IconButton diameter={32} aria-label="Edit HSN rate" className="border border-line text-muted hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
          onClick={(e) => { e.stopPropagation(); setEditing(r); setOpen(true); }}>
          <Pencil size={16} />
        </IconButton>
      ),
    } as DataTableColumn<any>] : []),
  ];

  return (
    <div>
      <PageHeader
        title="Tax masters"
        subtitle="HSN codes and their GST rates by effective period."
        actions={
          canManage ? (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => { setEditing(null); setOpen(true); }}>
              Add HSN rate
            </Button>
          ) : undefined
        }
      />
      {/* What this master is WORTH, before the rates themselves: how much of the catalogue it can
          actually price. The register below says what is configured; only this says what that covers. */}
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          tone="brand" icon={<CircleCheck />}
          label="Products priced" value={priced}
          hint={coverage ? `of ${coverage.length} active` : 'loading…'}
        />
        <StatCard
          tone={blocked.length ? 'danger' : 'neutral'} icon={<TriangleAlert />}
          label="Cannot be priced" value={blocked.length}
          hint={blocked.length ? 'their orders would be refused' : 'nothing is blocked'}
        />
        <StatCard
          tone="sky" icon={<Percent />}
          label="Rates in force" value={inForce}
          hint={`across ${distinctHsns} HSN${distinctHsns === 1 ? '' : 's'}`}
        />
        <StatCard
          tone="neutral" icon={<History />}
          label="Superseded" value={superseded}
          hint="kept for past documents"
        />
      </div>

      {/* THE GAP. Rates below; the products those rates do not yet cover, here — because an HSN with no
          row is not a tidiness problem, it is an order the GST engine will refuse. */}
      {coverage && (
        blocked.length === 0 ? (
          <Alert className="mb-4" type="success"
            title={`Every active product has a usable GST rate (${coverage.length} products)`}
            description="Nothing in the catalogue would be refused for want of a tax configuration." />
        ) : (
          // Same amber-band treatment as the GST readiness gate: this is a blocking condition, so it
          // should look like one rather than like a white card with a small triangle in the title.
          <section className="mb-4 overflow-hidden rounded-card border border-warning-300 bg-surface shadow-card dark:border-warning-500/40">
            <div className="flex flex-wrap items-start gap-3 border-b border-warning-300 bg-warning-50 px-4 py-3.5 dark:border-warning-500/30 dark:bg-warning-500/10">
              <span className="grid size-9 shrink-0 place-items-center rounded-control bg-warning-500/15 text-warning-600 dark:text-harvest-300">
                <TriangleAlert className="size-5" />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="type-section-title text-content">
                  {blocked.length} active product{blocked.length === 1 ? '' : 's'} cannot be priced
                </h2>
                <p className="mt-0.5 type-body text-muted">
                  Orders for these are refused rather than taxed at 0% — a missing rate is not an exemption.
                  Configure {blockedHsns.length === 1 ? 'the HSN' : `the ${blockedHsns.length} HSNs`} below, or record an
                  explicit exempt / nil-rated treatment.
                </p>
              </div>
              <span className="shrink-0 rounded-pill bg-warning-500/20 px-2.5 py-1 type-label-strong text-warning-700 dark:text-harvest-300">
                {blockedHsns.length + noHsn.length} to fix
              </span>
            </div>
            <div className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead className="gb-thead-tint">
                  <tr>
                    <th className="px-3 py-2 text-left type-overline text-muted">HSN</th>
                    <th className="px-3 py-2 text-left type-overline text-muted">Products</th>
                    <th className="px-3 py-2 text-left type-overline text-muted">Problem</th>
                    {canManage && <th className="px-3 py-2 text-right type-overline text-muted">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {blockedHsns.map((hsn) => {
                    const rows = blocked.filter((c) => c.hsn === hsn);
                    return (
                      <tr key={hsn} className="border-t border-line align-top">
                        <td className="px-3 py-2.5 font-mono text-content">{hsn}</td>
                        <td className="px-3 py-2.5 text-content">
                          {rows.slice(0, 4).map((r) => r.sku).join(', ')}
                          {rows.length > 4 && <span className="text-muted"> +{rows.length - 4} more</span>}
                        </td>
                        <td className="px-3 py-2.5 text-danger-600">No rate in force for this HSN</td>
                        {canManage && (
                          <td className="px-3 py-2.5 text-right">
                            <Button size="sm" variant="primary" className="w-32"
                              onClick={() => { setEditing(null); setPrefillHsn(hsn); setOpen(true); }}>
                              Configure
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {noHsn.map((r) => (
                    <tr key={r.productId} className="border-t border-line">
                      <td className="px-3 py-2.5 text-subtle">—</td>
                      <td className="px-3 py-2.5 text-content">{r.sku} · {r.name}</td>
                      <td className="px-3 py-2.5 text-danger-600">The product has no HSN code</td>
                      {canManage && (
                        <td className="px-3 py-2.5 text-right">
                          {/* Was dead grey text, so this row alone offered no way forward while every
                              row above it had a button. The HSN really is set on the product — so
                              send them there instead of just saying so. */}
                          <Tooltip label="An HSN is set on the product record, not in this master">
                            <Link
                              to="/products"
                              className="inline-flex h-8 w-32 items-center justify-center gap-1 whitespace-nowrap rounded-control border border-line bg-surface px-2.5 type-label text-brand-700 transition-colors hover:border-brand-400 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-900/20"
                            >
                              Open product <ArrowRight size={13} />
                            </Link>
                          </Tooltip>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
          </section>
        )
      )}

      <Card bodyClassName="p-0 sm:p-0">
        <div className="p-4">
          <TwDataTable
            storageKey="tax-masters"
            rowKey="id"
            loading={isLoading}
            dataSource={data ?? []}
            columns={columns}
            sortState={sortState}
            onSortChange={onSortChange}
            onRow={(r) => ({ onClick: canManage ? () => { setEditing(r); setOpen(true); } : undefined })}
            pagination={{ pageSize: 25, showSizeChanger: true }}
          />
        </div>
      </Card>

      <TaxFormModal open={open} record={editing} prefillHsn={prefillHsn}
        onClose={() => { setOpen(false); setEditing(null); setPrefillHsn(null); }} />
    </div>
  );
}

function TaxFormModal({
  open, onClose, record, prefillHsn,
}: { open: boolean; onClose: () => void; record?: any; prefillHsn?: string | null }) {
  const [form] = useForm();
  const qc = useQueryClient();
  const isEdit = !!record;
  // A non-taxable treatment charges nothing, so the rate inputs are locked to 0 rather than left for
  // someone to fill in — the server rejects a nil-rated row that carries a rate.
  const status = Form.useWatch('taxStatus', form) ?? 'TAXABLE';
  const nonTaxable = status && status !== 'TAXABLE';

  // Seed via initialValues + a per-target key (below) so the form mounts fresh with the right values
  // every open — avoids the setFieldsValue-in-effect race against the Radix-modal remount (blank fields
  // and a prior HSN's values leaking in → duplicate-key on save).
  const initialValues = record
    ? {
        ...record,
        effectiveFrom: record.effectiveFrom ? dayjs(record.effectiveFrom) : null,
        effectiveTo: record.effectiveTo ? dayjs(record.effectiveTo) : null,
        cgstPct: Number(record.cgstPct ?? 0),
        sgstPct: Number(record.sgstPct ?? 0),
        igstPct: Number(record.igstPct ?? 0),
      }
    // Opened from a product that cannot be priced: start on its HSN so the fix is one field away.
    : { effectiveFrom: dayjs(), taxStatus: 'TAXABLE', hsnCode: prefillHsn ?? undefined };

  const save = useConfirmedMutation({ title: "Save this tax master?", content: "It drives the tax worked out on new invoices.", okText: "Yes, save" }, {
    mutationFn: (vals: any) => {
      const payload = {
        ...vals,
        effectiveFrom: vals.effectiveFrom?.format('YYYY-MM-DD'),
        effectiveTo: vals.effectiveTo?.format('YYYY-MM-DD') ?? null,
        cgstPct: String(vals.cgstPct ?? 0),
        sgstPct: String(vals.sgstPct ?? 0),
        igstPct: String(vals.igstPct ?? 0),
      };
      if (payload.taxStatus && payload.taxStatus !== 'TAXABLE') {
        payload.cgstPct = '0'; payload.sgstPct = '0'; payload.igstPct = '0';
      }
      return isEdit
        ? request({ url: `/tax-masters/${record.id}`, method: 'PUT', data: payload })
        : request({ url: '/tax-masters', method: 'POST', data: payload });
    },
    onSuccess: () => {
      message.success(isEdit ? 'Updated' : 'Created');
      qc.invalidateQueries({ queryKey: ['tax-masters-all'] });
      qc.invalidateQueries({ queryKey: ['tax-masters'] });
      qc.invalidateQueries({ queryKey: ['tax-coverage'] });   // the gap panel re-reads itself
      qc.invalidateQueries({ queryKey: ['gst-health'] });      // and so does the GST readiness gate
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={isEdit ? `Edit · HSN ${record?.hsnCode}` : 'New tax rate'}
      width={640}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>{isEdit ? 'Update' : 'Create'}</Button>
        </>
      }
    >
      <Form key={record?.id ?? 'new'} form={form} layout="vertical" initialValues={initialValues} onFinish={(v) => save.mutate(v)}>
        <Row gutter={16}>
          <Col span={10}>
            <Form.Item name="hsnCode" label="HSN code"
              rules={[{ required: true }, { pattern: /^\d{4}(\d{2}(\d{2})?)?$/, message: 'HSN must be 4, 6 or 8 digits' }]}>
              <Input placeholder="38249990" maxLength={10} />
            </Form.Item>
          </Col>
          <Col span={14}>
            <Form.Item name="description" label="Description" rules={[{ required: true }]}>
              <Input placeholder="Bio-stimulant other" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="taxStatus" label="Treatment"
              extra="An explicit decision. The GST engine refuses an HSN with no row at all — it never treats a missing configuration as 0%.">
              <Select
                style={{ width: '100%' }}
                options={[
                  { value: 'TAXABLE', label: 'Taxable — charge the rates below' },
                  { value: 'EXEMPT', label: 'Exempt — no GST, by exemption' },
                  { value: 'NIL_RATED', label: 'Nil-rated — the rate itself is nil' },
                  { value: 'ZERO_RATED', label: 'Zero-rated — export / SEZ supply' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="cgstPct" label="CGST %" rules={[{ required: !nonTaxable }]}>
              <InputNumber className="w-full" min={0} max={50} step={0.5} disabled={nonTaxable} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="sgstPct" label="SGST %" rules={[{ required: !nonTaxable }]}>
              <InputNumber className="w-full" min={0} max={50} step={0.5} disabled={nonTaxable} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="igstPct"
              label="IGST %"
              dependencies={['cgstPct', 'sgstPct']}
              rules={[
                { required: true },
                (f: any) => ({
                  warningOnly: true, // usually IGST = CGST + SGST, but allow rare overrides (cess etc.)
                  validator: (_: unknown, v: any) => {
                    const c = Number(f.getFieldValue('cgstPct') ?? 0);
                    const s = Number(f.getFieldValue('sgstPct') ?? 0);
                    if (v != null && Math.abs(Number(v) - (c + s)) > 0.001) {
                      return Promise.reject(new Error(`Usually IGST = CGST + SGST (${c + s}%)`));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber className="w-full" min={0} max={50} step={0.5} />
            </Form.Item>
          </Col>
          {/* One-up below 576px: two date fields (~156px floor each) overlapped in a 320px dialog. */}
          <Col xs={24} sm={12}>
            <Form.Item name="effectiveFrom" label="Effective from" rules={[{ required: true }]}>
              <DatePicker className="w-full" format="DD MMM YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="effectiveTo"
              label="Effective to (blank = current)"
              dependencies={['effectiveFrom']}
              rules={[
                (f: any) => ({
                  validator: (_: unknown, v: any) => {
                    const from = f.getFieldValue('effectiveFrom');
                    if (v && from && v.isBefore(from, 'day')) {
                      return Promise.reject(new Error('Must be on or after "Effective from"'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker className="w-full" format="DD MMM YYYY" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
